// assets/quiz-tracking.js
const ONCE_PER_SESSION = new Set(['quiz_started', 'quiz_completed', 'quiz_abandoned']);
const ONCE_PER_QUESTION = new Set(['quiz_question_viewed']);

const META_EVENTS = {
  quiz_started: 'InitiateCheckout',
  quiz_completed: 'Lead',
  quiz_add_stack: 'AddToCart',
  quiz_email_captured: 'Lead',
};
const KLAVIYO_EVENTS = {
  quiz_started: 'Quiz Started',
  quiz_completed: 'Quiz Completed',
  quiz_abandoned: 'Quiz Abandoned',
  quiz_add_stack: 'Quiz Add Stack',
  quiz_email_captured: 'Quiz Plan Emailed',
};

export class QuizTracking {
  constructor({ sessionId, version, getReplayUrl } = {}) {
    this.sessionId = sessionId;
    this.version = version;
    this._getReplayUrl = getReplayUrl || (() => null);
    this._fired = new Set();
    window.dataLayer = window.dataLayer || [];
    if (typeof window !== 'undefined') window._quizTracking = this;
  }

  canTrack() {
    try { return window.Shopify?.customerPrivacy?.userCanBeTracked?.() ?? false; }
    catch { return false; }
  }

  _dedupKey(name, props) {
    if (ONCE_PER_SESSION.has(name)) return name;
    if (ONCE_PER_QUESTION.has(name)) return `${name}:${props.question_id || ''}`;
    return null;
  }

  emit(name, props = {}) {
    const key = this._dedupKey(name, props);
    if (key && this._fired.has(key)) return;
    if (key) this._fired.add(key);

    const payload = {
      event: name,
      quiz_session_id: this.sessionId,
      quiz_version: this.version,
      ...props,
    };

    if (!this.canTrack()) return;

    window.dataLayer.push(payload);

    if (META_EVENTS[name] && typeof window.fbq === 'function') {
      window.fbq('track', META_EVENTS[name], payload);
    }

    if (KLAVIYO_EVENTS[name] && window.klaviyo?.track) {
      window.klaviyo.track(KLAVIYO_EVENTS[name], payload);
    }

    if (name === 'quiz_completed' && this._email && window.klaviyo?.identify) {
      window.klaviyo.identify({
        '$email': this._email,
        quiz_goal: props.answers?.goal,
        quiz_tenure: props.answers?.tenure,
        quiz_sessions_per_week: props.answers?.sessions_per_week,
        quiz_sport: props.answers?.sport,
        quiz_bodyweight_band: props.answers?.bodyweight,
        quiz_diet_restrictions: props.answers?.diet,
        quiz_current_stack: props.answers?.current_stack,
        quiz_priorities: props.answers?.priorities,
        quiz_budget_band: props.answers?.budget,
        quiz_recommended_stack: props.stack_skus,
        quiz_completed_at: new Date().toISOString(),
        quiz_replay_url: props.replay_url,
      });
    }
  }

  setEmail(email) { this._email = email; }

  startAbandonWatch(getState) {
    if (this._abandonStarted) return;
    this._abandonStarted = true;
    let idleTimer;
    const resetIdle = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => this._maybeAbandon(getState()), 30 * 60 * 1000);
    };
    ['click','keydown','scroll','mousemove'].forEach(ev =>
      window.addEventListener(ev, resetIdle, { passive: true }));
    window.addEventListener('beforeunload', () => this._maybeAbandon(getState()));
    resetIdle();
  }

  _maybeAbandon(state) {
    if (!this.canTrack()) return;
    if (this._fired.has('quiz_abandoned') || this._fired.has('quiz_completed')) return;
    if (!this._email) return;
    const answers = state.getAnswers();
    if (!answers || Object.keys(answers).length === 0) return;
    const replayUrl = this._getReplayUrl(answers, state);
    this.emit('quiz_abandoned', {
      last_question_id: Object.keys(answers).at(-1),
      replay_url: replayUrl,
    });
  }
}

if (typeof window !== 'undefined') window.QuizTracking = QuizTracking;
